// src/hooks/useSkills.js
// State management hook for user skills

import { useState, useEffect, useCallback } from 'react';
import skillService from '../services/skillService';

export const useSkills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // The skill currently selected for the active chat session
  // null = use default (or none), a skill_id string = explicit selection
  const [selectedSkillId, setSelectedSkillId] = useState(null);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await skillService.listSkills();
      setSkills(data.skills || []);
    } catch (err) {
      console.error('Failed to fetch skills:', err);
      setError(err.message);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createSkill = useCallback(async ({ name, content, description, is_default }) => {
    try {
      const result = await skillService.createSkill({ name, content, description, is_default });
      if (result.success) {
        await fetchSkills();
        return { success: true, skill_id: result.skill_id };
      }
      return { success: false, message: result.error };
    } catch (err) {
      console.error('Failed to create skill:', err);
      return {
        success: false,
        message: err.message,
        violations: err.data?.violations || [],
      };
    }
  }, [fetchSkills]);

  const updateSkill = useCallback(async (skillId, updates) => {
    try {
      const result = await skillService.updateSkill(skillId, updates);
      if (result.success) {
        await fetchSkills();
        return { success: true };
      }
      return { success: false, message: result.error };
    } catch (err) {
      console.error('Failed to update skill:', err);
      return {
        success: false,
        message: err.message,
        violations: err.data?.violations || [],
      };
    }
  }, [fetchSkills]);

  const deleteSkill = useCallback(async (skillId) => {
    try {
      await skillService.deleteSkill(skillId);
      // If the deleted skill was selected, clear selection
      if (selectedSkillId === skillId) {
        setSelectedSkillId(null);
      }
      await fetchSkills();
      return { success: true };
    } catch (err) {
      console.error('Failed to delete skill:', err);
      return { success: false, message: err.message };
    }
  }, [fetchSkills, selectedSkillId]);

  const setDefault = useCallback(async (skillId) => {
    try {
      await skillService.setDefault(skillId);
      await fetchSkills();
      return { success: true };
    } catch (err) {
      console.error('Failed to set default:', err);
      return { success: false, message: err.message };
    }
  }, [fetchSkills]);

  const clearDefault = useCallback(async () => {
    try {
      await skillService.clearDefault();
      await fetchSkills();
      return { success: true };
    } catch (err) {
      console.error('Failed to clear default:', err);
      return { success: false, message: err.message };
    }
  }, [fetchSkills]);

  const uploadSkill = useCallback(async (file, options = {}) => {
    try {
      const result = await skillService.uploadSkill(file, options);
      if (result.success) {
        await fetchSkills();
        return { success: true, skill_id: result.skill_id };
      }
      return { success: false, message: result.error };
    } catch (err) {
      console.error('Failed to upload skill:', err);
      return {
        success: false,
        message: err.message,
        violations: err.data?.violations || [],
      };
    }
  }, [fetchSkills]);

  /**
   * Get the currently effective skill:
   * - If selectedSkillId is set, return that skill
   * - Otherwise return the default skill (if any)
   * - Otherwise return null
   */
  const getEffectiveSkill = useCallback(() => {
    if (selectedSkillId) {
      return skills.find(s => s.skill_id === selectedSkillId) || null;
    }
    return skills.find(s => s.is_default) || null;
  }, [skills, selectedSkillId]);

  /**
   * Get the effective skill_id to send with queries.
   * Returns null if no skill is active (neither selected nor default).
   */
  const getEffectiveSkillId = useCallback(() => {
    if (selectedSkillId) return selectedSkillId;
    const defaultSkill = skills.find(s => s.is_default);
    return defaultSkill ? defaultSkill.skill_id : null;
  }, [skills, selectedSkillId]);

  /**
   * Reset skill selection (used when starting a new conversation).
   * Does NOT change the default — only clears the per-session override.
   */
  const resetSelection = useCallback(() => {
    setSelectedSkillId(null);
  }, []);

  return {
    skills,
    loading,
    error,
    selectedSkillId,
    setSelectedSkillId,
    fetchSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    setDefault,
    clearDefault,
    uploadSkill,
    getEffectiveSkill,
    getEffectiveSkillId,
    resetSelection,
  };
};

export default useSkills;
